import React, { Fragment } from 'react'
import RoutedLink from 'src/components/RoutedLink'
import {
  notificationDuration as duration,
  endpointVersions
} from 'src/constants'
import { put, call, select } from 'redux-saga/effects'
import {
  replace,
  push,
  getLocation,
  onLocationChanged
} from 'connected-react-router'
import { actions as configActions } from 'src/features/config/ConfigSlice'
import { recursiveAddTokenToMenus } from 'src/features/config/ConfigHelpers'
import { actions as piwikActions } from 'src/features/piwik/PiwikSlice'
import { notificationsEnqueue } from 'src/features/notifications/NotificationsSlice'
import { successAccountSettingsLoad } from 'src/features/mySettings/MySettingsSlice'
import {
  ACCESS_DENIED,
  TOKEN_EXPIRED,
  DEVICE_LIMIT_REACHED,
  DEVICE_MISMATCH,
  DEVICE_UNKNOWN,
  DEVICE_UNKNOWN_CONFIRMATION_REQUIRED,
  CAPTCHA_VERIFICATION_REQUIRED,
  PROFILE_ALREADY_SUBSCRIBED,
  REGISTRATION_ALREADY_CONFIRMED
} from 'src/services/FW5ML/errorCodes'
import FW5MLError from 'src/services/FW5ML/FW5MLError'
import tokenTypes from 'src/services/session/tokenTypes'
import { setCaptchaLoginMeta } from 'src/features/captcha/CaptchaSlice'
import { LOGIN_TOKEN, NL_TOKEN } from 'src/features/captcha/constants'
import {
  SUCCEED_SSO_URL,
  FAILED_SSO_URL
} from 'src/services/FW5ML/constants/sso'
import { setNewsletterSettings } from 'src/features/myNewsletters/MyNewslettersSlice'
import newsletterTracking from '../../newsletterTracking'
import { authTokens } from '../../constants'

const { tokenGetEndpoint, tokenDeviceEndpoint } = endpointVersions.V2

/**
 * Logs debug information for bootstrap auth processes.
 *
 * @param {string} flow - The bootstrap flow being executed.
 * @param {string} action - The action being performed.
 * @param {Object} [details={}] - Additional details to log (optional, defaults to empty object).
 *
 * @returns {void}
 */
const debugLog = (flow, action, details = {}) => {
  console.debug(`[Bootstrap-Auth: ${flow}] ${action}`, details)
}

/**
 * Initializes bootstrap authentication by handling various authentication flows
 * based on different tokens present in the URL or session.
 *
 * @param {Function} services - Service container with dependencies
 *
 * Handles the following authentication flows:
 * - SSO authentication
 * - Registration confirmation
 * - Newsletter autologin
 * - Passwordless login
 * - Internal token authentication
 * - Previous session restoration
 * - Anonymous flow
 *
 * The function also handles URL cleanup by removing authentication tokens
 * and managing redirections if specified in the URL.
 *
 * @throws {Error} May throw errors from individual authentication flow handlers
 * @returns {void}
 */
export function* initializeBootstrapAuth(services) {
  debugLog('initialize', 'Starting bootstrap auth initialization')
  const queryString = services('queryString')
  const Session = services('Session')

  const location = yield select(state => state.router.location) // @TODO - Change function to getLocation when test supports browser history

  // Autologin token from Email
  const newsletterToken = queryString.get(authTokens.NEWSLETTER_TOKEN)

  // Passwordless Login Token from Newsletters
  const loginByEmailToken =
    queryString.get(authTokens.EMAIL_TOKEN_V1) ||
    queryString.get(authTokens.EMAIL_TOKEN_V2)

  // Registration Token
  const registrationToken = queryString.get(authTokens.REGISTRATION_TOKEN)

  // Internal token
  const internalToken = queryString.get(authTokens.INTERNAL_TOKEN)

  // Logout key
  const logoutKey = queryString.get(authTokens.LOGOUT_KEY)

  // Previous session token
  const sessionToken = yield call([Session, 'tokenLoad'])

  // Remove tokens from URL
  if (
    newsletterToken ||
    loginByEmailToken ||
    registrationToken ||
    internalToken ||
    logoutKey
  ) {
    const redirection = queryString.get('redir')
    if (redirection) {
      const newUrl = decodeURIComponent(redirection)
      yield put(
        onLocationChanged(
          { pathname: newUrl, search: '', hash: '' },
          'POP',
          false
        )
      )
      yield put(push(newUrl))
    } else {
      let newQS = ''
      // Remove token keys from url
      for (const token of Object.values(authTokens)) {
        newQS = queryString.removeKey(token, newQS)
      }

      debugLog('initialize', 'Retrieved tokens', {
        hasNewsletterToken: !!newsletterToken,
        hasLoginEmailToken: !!loginByEmailToken,
        hasRegistrationToken: !!registrationToken,
        hasInternalToken: !!internalToken,
        hasLogoutKey: !!logoutKey
      })

      yield put(replace({ search: newQS }))
    }
  }

  if (logoutKey) {
    // No previous or new session.
    yield call(authLogout, services)
    return
  }

  // SSO flow
  if ([SUCCEED_SSO_URL, FAILED_SSO_URL].includes(location?.pathname)) {
    yield call(authBySSOToken, services)
    return
  }

  // Registration Confirmation Flow
  if (registrationToken) {
    yield call(authByRegistrationToken, services, registrationToken)
    return
  }

  // NL Autologin Flow
  if (newsletterToken) {
    yield call(
      authByNewsletterToken,
      services,
      newsletterToken,
      undefined,
      undefined,
      location
    )
    return
  }

  // Passwordless Login Flow
  if (loginByEmailToken) {
    yield call(authByLoginEmailToken, services, loginByEmailToken)
    return
  }

  // Passwordless Login Flow
  if (internalToken) {
    yield call(authByInternalToken, services, internalToken)
    return
  }

  // Previous session. Open session
  if (sessionToken) {
    yield call(authByPreviousSessionToken, services, sessionToken)
    return
  }

  // No previous or new session.
  yield call(authByAnonFlow, services)
}

/**
 * Handles Phoenix token authentication process
 *
 * @param {Function} services - Service container with dependencies
 * @param {Object} user - User object containing authentication details
 * @param {string} access_token - Access token to be saved
 * @param {string} tokenType - Type of token being handled
 *
 * @throws {Error} When token saving fails
 * @returns {boolean} - Returns true if Phoenix token handling is successful, false otherwise
 */
export function* handlePhoenixToken(services, user, access_token, tokenType) {
  debugLog('phoenix', 'Starting Phoenix token handling', {
    hasUser: !!user,
    hasPartyId: !!user?.party_id,
    hasToken: !!access_token,
    tokenType
  })
  const PhoenixAuthService = services('PhoenixAuthService')
  const Session = services('Session')

  if (!user?.party_id && tokenType !== tokenTypes.TOKEN_TYPE_ANON) {
    debugLog(
      'phoenix',
      'Skipping Phoenix token - no party ID for non-anon user'
    )
    return false
  }

  let phoenixToken = null

  try {
    const authParams = user?.party_id ? { partyId: user.party_id } : {}
    const phoenixTokenRes = yield call(
      [PhoenixAuthService, 'getPhoenixToken'],
      tokenGetEndpoint,
      authParams
    )
    phoenixToken = phoenixTokenRes?.access
  } catch (error) {
    console.error('Failed to get Phoenix token:', error)
  }

  // Save the access token regardless of Phoenix token success/failure
  try {
    yield call([Session, 'tokenSave'], access_token, tokenType, phoenixToken)
    return true
  } catch (error) {
    debugLog('phoenix', 'Phoenix token handling failed', {
      error: error.message,
      stack: error.stack
    })
    return false
  }
}

/**
 * Logs in using a token sent via query string in "access_token"
 * from a link in a login email.
 *
 * @param {Function} services - Service container with dependencies
 * @param {string} token - Authentication token received from email
 * @param {string|null} captchaToken - Optional captcha verification token
 *
 * @throws {FW5MLError} Throws various error types for different authentication failures
 * @returns {void}
 */
export function* authByLoginEmailToken(services, token, captchaToken) {
  debugLog('emailLogin', 'Starting email login flow', {
    hasCaptchaToken: !!captchaToken
  })
  const UserRepository = services('UserRepository')
  const Session = services('Session')
  const Piwik = services('Piwik')

  try {
    const {
      access_token,
      data: { profile }
    } = yield call(
      [UserRepository, 'passwordlessLoginAuth'],
      token,
      captchaToken
    )
    debugLog('emailLogin', 'Login successful', {
      hasAccessToken: !!access_token,
      hasProfile: !!profile,
      profileId: profile?.profile_id
    })

    yield call([Session, 'tokenSave'], access_token, tokenTypes.TOKEN_TYPE_FULL)
    debugLog('emailLogin', 'Token saved to session')

    if (profile) {
      const user = yield call(processProfile, services, {
        profile,
        token: access_token
      })

      yield call(
        handlePhoenixToken,
        services,
        user,
        access_token,
        tokenTypes.TOKEN_TYPE_FULL
      )
    }

    yield call([Piwik, 'track'], 'login', 'login', 'sign-in')
    yield call(currentFlowRedirect, services, null)
  } catch (e) {
    // Previous session token
    const sessionToken = yield call([Session, 'tokenLoad'])
    if (sessionToken) {
      // Previous session. Open session
      yield call(authByPreviousSessionToken, services, sessionToken)
    } else {
      yield call([Piwik, 'track'], 'login', 'login', 'failed')
      // Use an anon token

      if (e instanceof FW5MLError) {
        const code = e.getCode()
        if ([TOKEN_EXPIRED, ACCESS_DENIED].includes(code)) {
          yield call([Session, 'destroySession'])
          yield put(
            notificationsEnqueue({
              message: (
                <Fragment>
                  Your link to sign in to the site expired. Please request a new
                  one by <RoutedLink label="clicking here" to="/sign-in" />.
                </Fragment>
              )
            })
          )
          yield call(authByAnonFlow, services)
          return
        } else if ([DEVICE_MISMATCH, DEVICE_UNKNOWN].includes(code)) {
          yield call(authByAnonFlow, services)
          const location = yield select(getLocation)
          const { pathname } = location
          yield put(
            replace(`/device-sign-in?r=${encodeURIComponent(pathname)}`)
          )
          return
        } else if (code === DEVICE_LIMIT_REACHED) {
          yield call([Session, 'destroySession'])
          yield call(authByAnonFlow, services)
          yield put(push('/device-limit'))
          yield put(
            notificationsEnqueue({
              message: (
                <Fragment>
                  You have reached the maximum number of devices on your
                  account. Please <RoutedLink label="contact us" /> for further
                  information.
                </Fragment>
              ),
              duration
            })
          )
          yield call([Piwik, 'track'], 'notification', 'error', 'device-limit')
          return
        } else if (code === CAPTCHA_VERIFICATION_REQUIRED) {
          yield call([Session, 'destroySession'])
          yield call(authByAnonFlow, services)
          // Set captcha search state
          yield put(setCaptchaLoginMeta({ token, type: LOGIN_TOKEN }))
          // Redirect to captcha form
          yield put(replace(`/captcha`))
          return
        } else if ([DEVICE_UNKNOWN_CONFIRMATION_REQUIRED].includes(code)) {
          yield call(authByAnonFlow, services)
          yield put(replace('/confirmation-email-sent'))
          return
        }
      }
      throw e
    }
  }
}

/**
 * Authenticates a user using a registration token and handles the registration confirmation process
 *
 * @param {Function} services - Service container with dependencies
 * @param {string} token - Registration confirmation token
 *
 * @returns {void}
 * @throws {FW5MLError} When token is expired or registration was already confirmed
 */
function* authByRegistrationToken(services, token) {
  const UserRepository = services('UserRepository')
  const Session = services('Session')
  const Piwik = services('Piwik')
  try {
    const {
      access_token,
      data: { profile }
    } = yield call([UserRepository, 'confirmRegistration'], token)
    yield call([Session, 'tokenSave'], access_token, tokenTypes.TOKEN_TYPE_FULL)

    if (profile) {
      const user = yield call(processProfile, services, {
        profile,
        token: access_token
      })
      yield call(
        handlePhoenixToken,
        services,
        user,
        access_token,
        tokenTypes.TOKEN_TYPE_FULL
      )
    }

    const registerRedirect = '/register-newsletter-subscribe'
    yield call(currentFlowRedirect, services, registerRedirect)

    yield call([Piwik, 'track'], 'register', 'confirm', 'confirmed')
    let registerMessage = 'Your registration has been confirmed.'
    yield put(notificationsEnqueue({ message: registerMessage }))
    yield call([Piwik, 'track'], 'notification', 'message', registerMessage)
  } catch (e) {
    // Previous session token
    const sessionToken = yield call([Session, 'tokenLoad'])
    if (sessionToken) {
      // Previous session. Open session
      yield call(authByPreviousSessionToken, services, sessionToken)
    } else {
      yield call([Piwik, 'track'], 'register', 'confirm', 'failed')
      // Use an anon token
      if (
        e instanceof FW5MLError &&
        (e.getCode() === TOKEN_EXPIRED ||
          e.getCode() === REGISTRATION_ALREADY_CONFIRMED)
      ) {
        yield call([Session, 'destroySession'])
        yield call(authByAnonFlow, services)
      }
      throw e
    }
  }
}

/**
 * Logs in using a token sent via query string in "t" from a link
 * in a Newsletter piece
 *
 * @param {Function} services - Service container with dependencies
 * @param {string} token - Newsletter authentication token
 * @param {string} captchaToken - Captcha verification token
 * @param {Object} [options] - Optional parameters
 * @param {(string|Object|null)} [redirect] - Redirect URL or object containing pathname and search
 *
 * @returns {void}
 * @throws {FW5MLError} When authentication fails with specific error codes
 */
export function* authByNewsletterToken(
  services,
  token,
  captchaToken,
  { nlEventId: storedNlEventId, nlCampaignId: storedNlCampaignId } = {},
  redirect
) {
  debugLog('nlLogin', 'Starting newsletter login flow', {
    hasCaptchaToken: !!captchaToken,
    hasRedirect: !!redirect
  })
  const UserRepository = services('UserRepository')
  const Session = services('Session')
  const Piwik = services('Piwik')
  const queryString = services('queryString')

  const nlEventId = storedNlEventId || queryString.get('nle', null, true)
  const nlCampaignId = storedNlCampaignId || queryString.get('nlc', null, true)

  // Store preprompt in localStorage if present
  const preprompt = queryString.get('preprompt', null, true)
  if (preprompt && typeof window !== 'undefined') {
    try {
      window.localStorage.setItem('fwai:preprompt', preprompt)
    } catch (_) {}
  }
  // --------------------------------------------------------------

  // New Login flow could redirect user to new device confimation page
  const metadata = { redirection: redirect }
  const sessionToken = yield call([Session, 'tokenLoad'])
  let access_token = null

  try {
    let profile

    if (sessionToken) {
      access_token = sessionToken
      const { data } = yield call([UserRepository, 'getProfile'])
      profile = data
    } else {
      const response = yield call(
        [UserRepository, 'auth'],
        token,
        captchaToken,
        metadata,
        sessionToken
      )
      const data = response.data
      access_token = response.access_token
      profile = data.profile
      yield call(
        [Session, 'tokenSave'],
        access_token,
        tokenTypes.TOKEN_TYPE_FULL
      )
    }

    if (profile) {
      const user = yield call(processProfile, services, {
        profile,
        token: access_token
      })
      yield call(
        handlePhoenixToken,
        services,
        user,
        access_token,
        tokenTypes.TOKEN_TYPE_FULL
      )
    }

    if (nlEventId) {
      yield call([Piwik, 'updateCustomVariable'], 'event_id', nlEventId)
    }
    if (nlCampaignId) {
      yield call([Piwik, 'updateCustomVariable'], 'project_id', nlCampaignId)
    }

    // OneClickSubscribe Campaign Flow Validation
    const campaign_id = queryString.get('sub_to_id', null, true)
    const from_campaign_id = queryString.get('sub_from_id', null, true)
    const campaign_uuid = queryString.get('sub_to_uuid', null, true)
    const event_id = queryString.get('event_id', null, true)

    if (campaign_id && from_campaign_id && campaign_uuid && event_id) {
      yield call(
        oneClickSubscribe,
        services,
        campaign_id,
        from_campaign_id,
        campaign_uuid,
        event_id
      )
    } else if (redirect) {
      if (typeof redirect === 'object') {
        // Redirection comes from ML response
        const { pathname, search } = redirect
        const cleaned = queryString.removeKey('t', search)
        yield put(replace({ pathname, search: cleaned }))
      } else {
        // Redirection comes from NL url
        yield put(replace(redirect))
      }
    }

    yield call([Piwik, 'track'], 'login', 'nl-autologin', 'sign-in')
    yield put(piwikActions.newsletterPiwikRequest(newsletterTracking))
  } catch (e) {
    yield call([Piwik, 'track'], 'login', 'nl-autologin', 'failed')
    if (e instanceof FW5MLError) {
      const code = e.getCode()
      if ([TOKEN_EXPIRED, ACCESS_DENIED].includes(code)) {
        yield call([Session, 'destroySession'])
        yield put(
          notificationsEnqueue({
            message: (
              <Fragment>
                Your link to sign in to the site expired. Please request a new
                one by <RoutedLink label="clicking here" to="/sign-in" />.
              </Fragment>
            )
          })
        )
        yield call(authByAnonFlow, services)
        return
      } else if ([DEVICE_MISMATCH, DEVICE_UNKNOWN].includes(code)) {
        yield call(authByAnonFlow, services)
        const location = yield select(getLocation)
        const { pathname } = location
        yield put(replace(`/device-sign-in?r=${encodeURIComponent(pathname)}`))
        return
      } else if (code === CAPTCHA_VERIFICATION_REQUIRED) {
        yield call([Session, 'destroySession'])
        yield call(authByAnonFlow, services)
        const location = yield select(state => state.router.location)
        yield put(
          setCaptchaLoginMeta({
            token,
            type: NL_TOKEN,
            redirect: location,
            nlEventId,
            nlCampaignId
          })
        )
        yield put(replace('/captcha'))
        return
      } else if ([DEVICE_UNKNOWN_CONFIRMATION_REQUIRED].includes(code)) {
        yield call(authByAnonFlow, services)
        yield put(replace('/confirmation-email-sent'))
        return
      }
    }
    throw e
  }
}

/**
 * Generator function that handles authentication using SSO token.
 * Processes successful and failed SSO authentication attempts.
 *
 * @param {Function} services - Service container with dependencies
 *
 * @returns {void}
 *
 * @description
 * For successful SSO:
 * - Saves access token
 * - Retrieves and processes user profile
 * - Handles Phoenix token
 * - Tracks successful login
 * - Redirects based on search API cookie or current flow
 *
 * For failed SSO:
 * - Tracks failed login attempt
 * - Initiates anonymous flow
 * - Displays error notification
 * - Redirects to appropriate page
 */
export function* authBySSOToken(services) {
  const Session = services('Session')
  const queryString = services('queryString')
  const UserRepository = services('UserRepository')
  const Piwik = services('Piwik')
  const location = yield select(state => state.router.location) // @TODO - Change function to getLocation when test supports browser history
  const { pathname } = location
  const redirect = queryString.get('relay_state') || '/'

  if (pathname === SUCCEED_SSO_URL) {
    // SSO succeded, save access token
    const access_token = queryString.get('atk')
    yield call([Session, 'tokenSave'], access_token, tokenTypes.TOKEN_TYPE_FULL)
    const { data: profile } = yield call([UserRepository, 'getProfile'])

    if (profile) {
      const user = yield call(processProfile, services, {
        profile,
        token: access_token
      })
      yield call(
        handlePhoenixToken,
        services,
        user,
        access_token,
        tokenTypes.TOKEN_TYPE_FULL
      )
    }

    yield call([Piwik, 'track'], 'login', 'sso-login', 'succeded')
    let searchApiCookie = yield call(
      [Session, 'getCookie'],
      'search_api_cookie'
    )
    /* The above code is checking if the `searchApiCookie` variable exists and is not an empty string.
      If it does exist and is not empty, it uses the `put` function to dispatch an action to replace
      the value of `searchApiCookie`. */
    if (searchApiCookie && searchApiCookie !== '') {
      yield put(replace(searchApiCookie))
      return
    }

    yield call(currentFlowRedirect, services, redirect)
  } else if (pathname === FAILED_SSO_URL) {
    // SSO failed, display error and start anon flow
    const errorMessage = queryString.get('error_msg').replace('+', ' ')
    yield call([Piwik, 'track'], 'login', 'sso-login', 'failed')
    yield call(authByAnonFlow, services)
    yield put(
      notificationsEnqueue({
        message: <Fragment>{errorMessage}</Fragment>,
        duration
      })
    )
    if (redirect !== '/') {
      yield put(replace(redirect))
    } else {
      yield put(replace('/sign-in'))
    }
  }
}

/**
 * Logs in using a token sent via query string in "it"
 * from a link in the other site
 *
 * @param {Function} services - Service container with dependencies
 * @param {string} access_token - The internal access token for authentication
 *
 * @returns {void}
 * @throws {Error} If authentication fails and no previous session exists
 */
export function* authByInternalToken(services, access_token) {
  const Session = services('Session')
  const UserRepository = services('UserRepository')
  const Piwik = services('Piwik')

  try {
    yield call([Session, 'tokenSave'], access_token, tokenTypes.TOKEN_TYPE_FULL)
    const { data: profile } = yield call([UserRepository, 'getProfile'])

    if (profile) {
      const user = yield call(processProfile, services, {
        profile,
        token: access_token
      })
      yield call(
        handlePhoenixToken,
        services,
        user,
        access_token,
        tokenTypes.TOKEN_TYPE_FULL
      )
    }

    yield call([Piwik, 'track'], 'login', 'internal-login', 'sign-in')
    yield call(currentFlowRedirect, services, null)
  } catch (e) {
    // Previous session token
    const sessionToken = yield call([Session, 'tokenLoad'])
    if (sessionToken) {
      // Previous session. Open session
      yield call(authByPreviousSessionToken, services, sessionToken)
    } else {
      yield call([Piwik, 'track'], 'login', 'internal-login', 'failed')
      if (e instanceof FW5MLError) {
        yield call([Session, 'destroySession'])
        yield call(authByAnonFlow, services)
        return
      }
      throw e
    }
  }
}

/**
 * Handles the anonymous authentication flow.
 *
 * @param {Function} services - Service container with dependencies
 *
 * @returns {void}
 */
export function* authByAnonFlow(services) {
  const UserRepository = services('UserRepository')
  const Session = services('Session')
  const Piwik = services('Piwik')
  const DeviceIdService = services('DeviceIdService')
  const SessionService = services('SessionService')
  const { access_token } = yield call([UserRepository, 'auth'])
  const CatalogsRepository = services('CatalogsRepository')
  const configs = yield call([CatalogsRepository, 'getCatalog'], 'configs') //User for ML connection
  console.log('Anon access token', access_token, configs)

  yield call(
    handlePhoenixToken,
    services,
    {},
    access_token,
    tokenTypes.TOKEN_TYPE_ANON
  )

  const deviceId = yield call(ensureDeviceId, services, null)

  let trackingPartyId = 0
  let partySource = ''
  if (deviceId) {
    try {
      const response = yield call(
        [DeviceIdService, 'getSingleDeviceToken'],
        tokenDeviceEndpoint
      )
      trackingPartyId = response?.partyId
      partySource = response?.partySource
    } catch (error) {
      console.warn('[authByAnonFlow] Failed to get device token:', error)
    }
  }

  // TODO: change call
  const menuData = recursiveAddTokenToMenus(configs?.menus, null, null)
  yield put(configActions.addConfigSuccess({ key: 'menus', data: menuData }))

  yield call([SessionService, 'generateAnonSessionId'])
  yield call([Session, 'invalidateSession'])

  yield call([Piwik, 'updateCustomVariable'], 'psl_device_id', deviceId)
  yield call([Piwik, 'updateCustomVariable'], 'MemberID', trackingPartyId)
  yield call([Piwik, 'updateCustomVariable'], 'PartySource', partySource)
  yield call([Piwik, 'updateCustomVariable'], 'EmailID', 0)
  yield call([Piwik, 'updateCustomVariable'], 'site_uid', 0)
}

/**
 * not you? register here! flow
 *
 * @param {Function} services - Service container with dependencies
 *
 * @returns {void}
 */
export function* authLogout(services) {
  const Session = services('Session')
  const ClarityService = services('ClarityService')
  const queryString = services('queryString')
  const redirect = queryString.get('redirect') || 'register'
  yield call([Session, 'destroySession'])
  yield call(authByAnonFlow, services)
  yield call([ClarityService, 'resetCustomIds'])
  yield put(replace(redirect))
}

/**
 * Authenticates a user using a previous session token
 *
 * @param {Function} services - Service container with dependencies
 * @param {string} token - Previous session token for authentication
 *
 * @throws {Error} Throws error if session restoration fails
 * @returns {void}
 */
function* authByPreviousSessionToken(services, token) {
  debugLog('session', 'Starting previous session restoration', {
    hasToken: !!token
  })

  const UserRepository = services('UserRepository')
  const Session = services('Session')
  const Piwik = services('Piwik')
  const ClarityService = services('ClarityService')

  try {
    debugLog('session', 'Loading user from session')
    const user = yield call([Session, 'userLoad'])
    debugLog('session', 'User load completed', {
      hasUser: !!user,
      userId: user?.profile_id,
      subscriptionType: user?.subscription_type
    })

    if (user) {
      debugLog('session', 'Processing authenticated user session')

      const { data: profile } = yield call([UserRepository, 'getProfile'])
      debugLog('session', 'Profile retrieved', {
        hasProfile: !!profile,
        profileId: profile?.profile_id,
        profileMatchesUser: profile?.profile_id === user?.profile_id
      })

      // Set piwik custom var to the user's profile_id
      yield call([Piwik, 'updateCustomVariable'], 'MemberID', user?.party_id)
      yield call(
        [Piwik, 'updateCustomVariable'],
        'EmailID',
        user?.party_email_id
      )
      yield call([Piwik, 'updateCustomVariable'], 'site_uid', user?.profile_id)
      debugLog('session', 'Updated tracking variables')

      if (profile) {
        yield call([Session, 'tokenSave'], token, tokenTypes.TOKEN_TYPE_FULL)
        debugLog('session', 'Token saved')

        const user = yield call(processProfile, services, { profile, token })

        debugLog('session', 'Handling Phoenix token')

        yield call(
          handlePhoenixToken,
          services,
          user,
          token,
          tokenTypes.TOKEN_TYPE_FULL
        )
      }
    } else {
      debugLog('session', 'No existing user session, starting anonymous flow')

      yield call([Session, 'destroySession'])
      debugLog('session', 'Session destroyed')

      yield call([ClarityService, 'resetCustomIds'])
      debugLog('session', 'Analytics IDs reset')

      yield call(authByAnonFlow, services)
      debugLog('session', 'Anonymous flow completed')
    }
  } catch (error) {
    debugLog('session', 'Session restoration failed', {
      error: error.message,
      code: error.getCode?.(),
      stack: error.stack
    })
    throw error
  }
}

/**
 * Processes a user profile and performs various operations including user parsing, session management, and tracking.
 *
 * @param {Function} services - Service container with dependencies
 * @param {Object} options - The options object
 *
 * @returns {Object|null} Returns the parsed user object if successful, null if profile is invalid
 * @throws {Error} May throw errors from service calls
 */
function* processProfile(services, { profile = null, token = null }) {
  // Check if profile is valid to continue the flow
  if (!profile?.profile_id) {
    return null
  }

  const UserParser = services('UserParser')
  const Session = services('Session')
  const Piwik = services('Piwik')
  const ClarityService = services('ClarityService')
  const UserRepository = services('UserRepository')
  const CatalogsRepository = services('CatalogsRepository')
  const configs = yield call([CatalogsRepository, 'getCatalog'], 'configs') //User for ML connection

  const user = yield call([UserParser, 'parseUser'], profile)

  // Get premium account consultant info
  if (user?.premiumAccountId) {
    try {
      const consultant = yield call(
        [UserRepository, 'getPremiumAccountConsultant'],
        user.premiumAccountId
      )

      if (consultant?.active) {
        user.premiumAccountConsultant = consultant
      }
    } catch (e) {
      console.error('Failed to get premium account consultant', e)
    }
  }

  // Get region name from country code
  if (user?.address?.country && typeof user.address.country === 'string') {
    try {
      const regionData = yield call(
        [UserRepository, 'getRegionByCountry'],
        user.address.country
      )

      if (regionData?.data?.[0]?.name) {
        user.region = regionData.data[0].name
      }
    } catch (e) {
      console.error('Failed to get region data', e)
    }
  }

  // Save profile newsletter settings
  if (profile.newsletter_settings) {
    yield put(
      setNewsletterSettings({
        newsletterSettings: profile.newsletter_settings
      })
    )
  }

  // Save profile account settings, ex: medical_abstracts
  if (profile?.account_settings) {
    yield put(successAccountSettingsLoad(profile.account_settings))
  }

  yield call([Session, 'userSave'], user)

  // Config related calls with user data
  if (token) {
    yield put(
      configActions.addConfigSuccess({ key: 'internal_token', data: token })
    )
  }

  const menuData = recursiveAddTokenToMenus(configs?.menus, token, user)
  yield put(configActions.addConfigSuccess({ key: 'menus', data: menuData }))

  // Tracking calls
  yield call(
    [Piwik, 'updateCustomVariable'],
    'fw_access_id',
    (user?.access_group_ids || []).join(',')
  )
  yield call([Piwik, 'updateCustomVariable'], 'MemberID', profile?.party_id)
  yield call(
    [Piwik, 'updateCustomVariable'],
    'EmailID',
    profile?.party_email_id
  )
  yield call([Piwik, 'updateCustomVariable'], 'site_uid', profile?.profile_id)

  // Initialize 3rd party services that depend on user data
  yield call(ensureDeviceId, services, user)

  yield call(
    [ClarityService, 'setCustomIds'],
    user?.party_id || user?.profile_id,
    user?.subscription_type
  )

  return user
}

/**
 * Generator function that manages device ID retrieval and tracking.
 * Attempts to get a device ID from session cookies, or fetches a new one if not found.
 * Updates Piwik tracking variables for device ID and user status.
 *
 * @param {Function} services - Service container with dependencies
 * @param {Object|null} [user=null] - User object containing profile information
 * @yields {Promise} Yields promises for async service calls
 *
 * @returns {string|null} The device ID if successfully retrieved, null otherwise
 * @throws {Error} When service calls fail
 */
function* ensureDeviceId(services, user = null) {
  const Session = services('Session')
  const Piwik = services('Piwik')
  const DeviceIdService = services('DeviceIdService')

  let deviceId = yield call([Session, 'getCookie'], 'PSL_DEVICE_ID')

  if (user?.profile_id) {
    yield call([Piwik, 'updateCustomVariable'], 'PartySource', 'Known')
  }

  if (deviceId) {
    yield call([Piwik, 'updateCustomVariable'], 'psl_device_id', deviceId)
    return deviceId
  }

  try {
    const result = yield call([DeviceIdService, 'getDeviceId'], user)
    deviceId = result?.deviceId

    if (!deviceId) {
      console.warn('[DeviceId] No valid device ID received')
      return null
    }

    yield call([Session, 'setCookie'], 'PSL_DEVICE_ID', deviceId)
    yield call([Piwik, 'updateCustomVariable'], 'psl_device_id', deviceId)
  } catch (e) {
    console.error('[DeviceId] Initialization failed:', e)
    return null
  }

  return deviceId
}

/**
 * Generator function to handle newsletter one-click newsletter subscription process used in Promotional Campaigns.
 * (Promotional Campaigns are an OLD NLB feature, to be deprecated)
 *
 * @param {Function} services - Service container with dependencies
 * @param {string|number} campaign_id - ID of the current campaign
 * @param {string|number} from_campaign_id - ID of the originating campaign
 * @param {string} campaign_uuid - UUID of the campaign to subscribe to
 * @param {string|number} event_id - ID of the event
 *
 * @returns {void}
 * @throws {Error} Throws error if subscription fails
 *
 * @description Attempts to subscribe user to a newsletter through one-click process:
 * 1. Retrieves all newsletters
 * 2. Finds specific newsletter by UUID
 * 3. Attempts to subscribe user
 * 4. Redirects to result page with appropriate status
 */
function* oneClickSubscribe(
  services,
  campaign_id,
  from_campaign_id,
  campaign_uuid,
  event_id
) {
  const UserRepository = services('UserRepository')
  const NewslettersRepository = services('NewslettersRepository')
  const ocsroute = '/newsletter-subscribe'
  let nls_all = []
  let newsletter = null

  try {
    nls_all = yield call([NewslettersRepository, 'getNewsletters'])
    newsletter = yield call(
      [NewslettersRepository, 'getNewsletterById'],
      nls_all,
      campaign_uuid
    )
    // Add newsletter to user
    yield call(
      [UserRepository, 'insertNewsletterOneClick'],
      campaign_uuid,
      parseInt(event_id),
      parseInt(campaign_id),
      parseInt(from_campaign_id)
    )
    yield put(
      replace(ocsroute, {
        result: 'subscribed',
        nlName: newsletter.name
      })
    )
  } catch (e) {
    console.log(e)
    const code = e.getCode()

    // User is already subscribed
    if (code === PROFILE_ALREADY_SUBSCRIBED && newsletter) {
      yield put(
        replace(ocsroute, {
          result: 'already-subscribed',
          nlName: newsletter.name
        })
      )
      return
    }

    yield put(
      replace(ocsroute, {
        result: 'error',
        errorCode: code
      })
    )
  }
}

/**
 * Generator function that handles redirection based on current flow cookie or specific redirect path
 * (used for landing pages or notify me redirections).
 *
 * @param {Function} services - Service container with dependencies
 * @param {string|null} [redirect=null] - Optional redirect path to use if no current flow cookie exists
 *
 * @returns {void}
 * @throws {Error} Logs any errors that occur during execution
 */
function* currentFlowRedirect(services, redirect = null) {
  try {
    const Session = services('Session')
    let currentFlowCookie = yield call([Session, 'getCookie'], 'current_flow')

    // current_flow cookie redirection
    if (currentFlowCookie && currentFlowCookie !== '') {
      const cookieSplit = currentFlowCookie.split('|')
      const route = cookieSplit[0]
      const additionalProps = cookieSplit[1]

      if (route === 'notify-me-subscribe') {
        yield put(replace(`/story/${additionalProps}`))
        return
      }
      yield put(replace(route))
      return
    }

    // Specific redirection in case the is no current_flow cookie defined
    if (redirect) {
      yield put(replace(redirect))
    }
  } catch (e) {
    console.log(e)
  }
}
